// Pure helpers behind the deal's custom fields and the Deals section. Kept out
// of the components so the rules (which deal the card edits, what an input
// sends) have tests.
import type { CustomValue, CustomValues, Deal } from '@/shared/api/deals'
import type { FieldDef, FieldEntity, PicklistDef, PicklistOptionDef } from '@/shared/api/processConfig'

/** The active definitions for one entity, in the admin's order. */
export function fieldsFor(fields: readonly FieldDef[], entity: FieldEntity): FieldDef[] {
  return fields
    .filter(f => f.entity === entity && !f.archivedAt)
    .sort((a, b) => a.position - b.position)
}

/** A picklist's active options in order, plus `keep` values a record already
 *  holds even though the option has since been archived. */
export function optionsFor(
  picklists: readonly PicklistDef[],
  picklistId: string | null,
  keep: readonly string[] = [],
): PicklistOptionDef[] {
  const list = picklists.find(p => p.id === picklistId)
  if (!list) return []
  return list.options
    .filter(o => !o.archivedAt || keep.includes(o.value))
    .sort((a, b) => a.position - b.position)
}

/**
 * The deal the lead card edits: the company's lead deal, the one its
 * crm_leads row stands for. A deal in the default pipeline only, the open
 * one, else the newest opened, else the highest id (lead_deal_id in
 * shared/v2/writes.py). Null when no deal is in the default pipeline.
 */
export function leadDeal(deals: readonly Deal[]): Deal | null {
  let best: Deal | null = null
  for (const d of deals) {
    if (d.pipeline.isDefault && (!best || leadsOver(d, best))) best = d
  }
  return best
}

function leadsOver(a: Deal, b: Deal): boolean {
  const aOpen = a.outcome === 'open'
  if (aOpen !== (b.outcome === 'open')) return aOpen
  const at = Date.parse(a.openedAt) - Date.parse(b.openedAt)
  if (at !== 0) return at > 0
  return a.id > b.id
}

/** Pipelines that already hold an open deal for this company. The API allows
 *  one open deal per pipeline, so these are not offered for a new one. */
export function pipelinesWithOpenDeal(deals: readonly Deal[]): Set<string> {
  return new Set(deals.filter(d => d.outcome === 'open').map(d => d.pipeline.id))
}

export type Draft = string | boolean | string[]

/** The value an input shows for a stored custom value. */
export function toDraft(field: FieldDef, value: CustomValue | undefined): Draft {
  if (field.type === 'bool') return value === true
  if (field.type === 'multi_select') return Array.isArray(value) ? value : []
  if (value === undefined || value === null) return ''
  if (field.type === 'date') return dayOf(value)
  return String(value)
}

/** The day a stored date names: its first ten characters. The deals API
 *  stores YYYY-MM-DD, and the mirror copies crm_leads.move_date as a UTC
 *  timestamp string, whose date is the day the lead card saved (it sends a
 *  date-only value, which the API stores at midnight UTC). */
function dayOf(value: CustomValue): string {
  return String(value).slice(0, 10)
}

/**
 * What an input's value sends. Blank sends null, which clears the key. A
 * number that does not parse goes as typed, so the API names the field in
 * its refusal instead of the value vanishing here.
 */
export function fromDraft(field: FieldDef, draft: Draft): CustomValue | null {
  if (field.type === 'bool') return draft === true
  if (field.type === 'multi_select') return Array.isArray(draft) && draft.length ? draft : null
  const text = typeof draft === 'string' ? draft.trim() : ''
  if (!text) return null
  if (field.type === 'number') {
    const n = Number(text.replace(',', '.'))
    return Number.isFinite(n) ? n : text
  }
  return text
}

function same(a: CustomValue | null | undefined, b: CustomValue | null | undefined): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i])
  }
  return (a ?? null) === (b ?? null)
}

/** The custom keys whose draft differs from what is stored, as a PATCH body.
 *  An unticked bool that was never set is not a change, and neither is a
 *  date naming the stored day. */
export function customPatch(
  fields: readonly FieldDef[],
  stored: CustomValues,
  drafts: Record<string, Draft>,
): Record<string, CustomValue | null> {
  const patch: Record<string, CustomValue | null> = {}
  for (const f of fields) {
    if (!(f.key in drafts)) continue
    const next = fromDraft(f, drafts[f.key])
    const before = stored[f.key]
    if (f.type === 'bool' && before === undefined && next === false) continue
    if (f.type === 'date' && before != null && next != null && dayOf(before) === dayOf(next)) continue
    if (!same(before, next)) patch[f.key] = next
  }
  return patch
}
